import { useState, type ChangeEvent, type ReactNode, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Step, StepConnector, StepContent, Stepper, StepLabel, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useChangePasswordV2, useCreateSudoPassword } from '@/generated/client/passwordreset/passwordreset';
import { getPasswordStrengthColor } from '@/components/common/passwordStrength';
import { usePasswordValidation } from '@/hooks/usePasswordValidation';
import { reportError } from '@/integrations/sentry/errorReporting';
import { useNotificationStore } from '@/stores/notificationstore';
import { getApiErrorMessageKeyForStatus, getApiErrorStatus } from '@/shared/api/apiError';
import type { PasswordChangeFlowProps, PasswordChangeStep } from '../../types/PasswordChange.types';
import { CurrentPasswordStep } from './CurrentPasswordStep';
import { NewPasswordStep } from './NewPasswordStep';
import { SuccessStep } from './SuccessStep';
import { useSuccessCountdown } from './useSuccessCountdown';

export { PASSWORD_CHANGE_SUCCESS_TIMEOUT_MS } from './useSuccessCountdown';

const PASSWORD_CHANGE_STEP_TRANSITION_TIMEOUT_MS = 300;
const STEP_INDEX: Record<PasswordChangeStep, number> = {
  'current-password': 0,
  'new-password': 1,
  success: 2,
};

const PasswordChangeStepLabel = styled(StepLabel)(({ theme }) => ({
  '& .MuiStepLabel-label': {
    color: theme.palette.text.secondary,
    '&.Mui-completed': { color: theme.palette.text.primary },
    '&.Mui-active': { color: theme.palette.secondary.main },
  },
  '& .MuiStepIcon-root': {
    color: theme.palette.grey[300],
    '&.Mui-completed': { color: theme.palette.success.main },
    '&.Mui-active': { color: theme.palette.secondary.main },
  },
}));

const PasswordChangeStepConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': { borderColor: theme.palette.grey[300] },
  '&.Mui-active .MuiStepConnector-line': { borderColor: theme.palette.success.main },
}));

export function PasswordChangeFlow({ username, isPhone, onFinished }: PasswordChangeFlowProps) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const showNotification = useNotificationStore((state) => state.showNotification);
  const [step, setStep] = useState<PasswordChangeStep>('current-password');
  const [currentPassword, setCurrentPassword] = useState('');
  const [sudoPassword, setSudoPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordAgain, setNewPasswordAgain] = useState('');
  const [currentPasswordError, setCurrentPasswordError] = useState<string>();

  function resetToCurrentPassword() {
    setStep('current-password');
    setCurrentPassword('');
    setCurrentPasswordError(undefined);
    setSudoPassword('');
    setNewPassword('');
    setNewPasswordAgain('');
  }

  function showUnexpectedError(error: unknown, operation: 'authenticate' | 'save') {
    reportError(error, { feature: 'password-change', operation });
    showNotification('error', t('login.passwordChangeV3.saveError'));
  }

  function handleValidationError(error: unknown) {
    const status = getApiErrorStatus(error);
    if (status === 401) {
      resetToCurrentPassword();
    }
    const messageKey = getApiErrorMessageKeyForStatus(status);
    if (messageKey) {
      showNotification('error', t(messageKey));
    }
  }

  function handleAuthenticateError(error: unknown) {
    const status = getApiErrorStatus(error);
    if (status === 401) {
      setCurrentPasswordError(t('login.passwordChangeV3.currentPasswordError'));
      return;
    }
    const messageKey = getApiErrorMessageKeyForStatus(status);
    if (messageKey) {
      showNotification('error', t(messageKey));
      return;
    }
    showUnexpectedError(error, 'authenticate');
  }

  function handleSaveError(error: unknown) {
    const status = getApiErrorStatus(error);
    if (status === 401) {
      resetToCurrentPassword();
      showNotification('error', t('error.api.err_unauthorized'));
      return;
    }
    if (status === 400 || status === 422) {
      showNotification('error', t('login.resetPassword.passwordFormatError'));
      return;
    }
    const messageKey = getApiErrorMessageKeyForStatus(status);
    if (messageKey) {
      showNotification('error', t(messageKey));
      return;
    }
    showUnexpectedError(error, 'save');
  }

  const authenticateMutation = useCreateSudoPassword({
    mutation: {
      retry: false,
      onSuccess: ({ data }) => {
        setSudoPassword(data);
        setCurrentPassword('');
        setStep('new-password');
      },
      onError: handleAuthenticateError,
    },
  });
  const saveMutation = useChangePasswordV2({
    mutation: {
      retry: false,
      onSuccess: () => {
        setSudoPassword('');
        setStep('success');
      },
      onError: handleSaveError,
    },
  });
  const isSubmitting = authenticateMutation.isPending || saveMutation.isPending;
  const { isAccepted, isPending, validation } = usePasswordValidation({
    username,
    newPassword: step === 'new-password' ? newPassword : '',
    sudoPassword,
    language: i18n.language,
    onError: handleValidationError,
  });
  const progressValue = Math.min(120, Math.max(0, validation?.strength ?? 0));
  const validationColor = getPasswordStrengthColor(theme, progressValue, isAccepted);
  const remainingMs = useSuccessCountdown(step === 'success', onFinished);

  function handleCurrentPasswordChange(event: ChangeEvent<HTMLInputElement>) {
    setCurrentPassword(event.target.value);
    setCurrentPasswordError(undefined);
  }

  function handleNewPasswordChange(event: ChangeEvent<HTMLInputElement>) {
    setNewPassword(event.target.value);
  }

  function handleNewPasswordAgainChange(event: ChangeEvent<HTMLInputElement>) {
    setNewPasswordAgain(event.target.value);
  }

  function handleCurrentPasswordSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!currentPassword) {
      setCurrentPasswordError(t('login.error.missingPassword'));
      return;
    }

    authenticateMutation.mutate({ data: { username, currentPassword } });
  }

  function handleNewPasswordSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isAccepted || !newPasswordAgain || newPasswordAgain !== newPassword) {
      return;
    }

    saveMutation.mutate({ data: { username, sudoPassword, newPassword } });
  }

  const activeStep = STEP_INDEX[step];
  const steps = [
    {
      id: 'current-password',
      label: t('login.passwordChangeV3.currentStepTitle'),
      content: (
        <CurrentPasswordStep
          currentPassword={currentPassword}
          currentPasswordError={currentPasswordError}
          isSubmitting={isSubmitting}
          onChange={handleCurrentPasswordChange}
          onSubmit={handleCurrentPasswordSubmit}
        />
      ),
    },
    {
      id: 'new-password',
      label: t('login.passwordChangeV3.newStepTitle'),
      content: (
        <NewPasswordStep
          isAccepted={isAccepted}
          isPending={isPending}
          isPhone={isPhone}
          isSubmitting={isSubmitting}
          newPassword={newPassword}
          newPasswordAgain={newPasswordAgain}
          validation={validation}
          validationColor={validationColor}
          onNewPasswordChange={handleNewPasswordChange}
          onNewPasswordAgainChange={handleNewPasswordAgainChange}
          onSubmit={handleNewPasswordSubmit}
        />
      ),
    },
    {
      id: 'success',
      label: t('login.passwordChangeV3.successTitle'),
      content: <SuccessStep remainingMs={remainingMs} onFinished={onFinished} />,
    },
  ] satisfies Array<{ id: PasswordChangeStep; label: string; content: ReactNode }>;

  return (
    <Stepper
      activeStep={activeStep}
      orientation="vertical"
      connector={<PasswordChangeStepConnector />}
      sx={{ width: '100%' }}
      data-qa-element-id="password-change-stepper"
    >
      {steps.map(({ id, label, content }, index) => (
        <Step key={id} completed={index < activeStep}>
          <PasswordChangeStepLabel>{label}</PasswordChangeStepLabel>
          <StepContent slotProps={{ transition: { timeout: PASSWORD_CHANGE_STEP_TRANSITION_TIMEOUT_MS } }}>
            {content}
          </StepContent>
        </Step>
      ))}
    </Stepper>
  );
}
